import apiClient from '../services/apiClient'

const handleApiError = (error: any, context: string) => {
    console.error(`API Error [${context}]:`, error)
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred'
    throw new Error(errorMessage)
}

// Products
export const getAllProducts = async (isActive?: boolean) => {
    try {
        return (await apiClient.get('/products', { params: isActive !== undefined ? { isActive } : undefined })).data
    } catch (error: any) {
        handleApiError(error, 'getAllProducts')
    }
}

export const getProduct = async (id: number, lng?: string) => {
    try {
        if (lng && lng !== 'fr') {
            return (await apiClient.get(`/products/${id}`, { params: { lng } })).data
        }
        return (await apiClient.get(`/products/${id}`)).data
    } catch (error: any) {
        handleApiError(error, 'getProduct')
    }
}

export const createProduct = async (productData: any) => {
    try {
        const formData = new FormData()

        // Add all product fields
        Object.keys(productData).forEach((key) => {
            if (key === 'images' && Array.isArray(productData[key])) {
                // Handle multiple images array
                productData[key].forEach((file: File) => {
                    if (file instanceof File) {
                        formData.append('images', file)
                    }
                })
            } else if (productData[key] !== undefined && productData[key] !== null) {
                formData.append(
                    key,
                    typeof productData[key] === 'object' ? JSON.stringify(productData[key]) : productData[key],
                )
            }
        })

        return (
            await apiClient.post('/products', formData, {
                params: { role: 'ROLE_ADMIN' },
                headers: { 'Content-Type': 'multipart/form-data' },
            })
        ).data
    } catch (error: any) {
        handleApiError(error, 'createProduct')
    }
}

export const updateProduct = async (id: number, productData: any, lng?: string) => {
    try {
        const formData = new FormData()

        // Add all product fields
        Object.keys(productData).forEach((key) => {
            if (key === 'images' && Array.isArray(productData[key])) {
                // Handle multiple images array
                productData[key].forEach((file: File) => {
                    if (file instanceof File) {
                        formData.append('images', file)
                    }
                })
            } else if (productData[key] !== undefined && productData[key] !== null) {
                formData.append(
                    key,
                    typeof productData[key] === 'object' ? JSON.stringify(productData[key]) : productData[key],
                )
            }
        })

        // Add language parameter if provided
        if (lng) {
            formData.append('lng', lng)
        }

        return (
            await apiClient.put(`/products/${id}`, formData, {
                params: { role: 'ROLE_ADMIN' },
                headers: { 'Content-Type': 'multipart/form-data' },
            })
        ).data
    } catch (error: any) {
        handleApiError(error, 'updateProduct')
    }
}

export const deleteProduct = async (id: number) => {
    try {
        return (await apiClient.delete(`/products/${id}`, { params: { role: 'ROLE_ADMIN' } })).data
    } catch (error: any) {
        handleApiError(error, 'deleteProduct')
    }
}

export const getProductStock = async (id: number) => {
    try {
        return (await apiClient.get(`/products/${id}/stock`)).data
    } catch (error: any) {
        handleApiError(error, 'getProductStock')
    }
}

export const updateProductStock = async (id: number, stock: number) => {
    try {
        return (await apiClient.put(`/products/${id}/stock`, { stock }, { params: { role: 'ROLE_ADMIN' } })).data
    } catch (error: any) {
        handleApiError(error, 'updateProductStock')
    }
}

export const canDeleteProduct = async (id: number) => {
    try {
        return (await apiClient.get(`/products/${id}/can-delete`)).data
    } catch (error: any) {
        handleApiError(error, 'canDeleteProduct')
    }
}

// Orders
export const createOrder = async (orderData: any, role?: string) => {
    try {
        const formData = new FormData()

        // Add all order fields
        Object.keys(orderData).forEach((key) => {
            if (key === 'paymentProof' && orderData[key] instanceof File) {
                formData.append('paymentProof', orderData[key])
            } else if (orderData[key] !== undefined && orderData[key] !== null) {
                formData.append(
                    key,
                    typeof orderData[key] === 'object' ? JSON.stringify(orderData[key]) : orderData[key],
                )
            }
        })

        return (
            await apiClient.post('/orders', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                ...(role && { params: { role } }),
            })
        ).data
    } catch (error: any) {
        handleApiError(error, 'createOrder')
    }
}

export const validateOrder = async (id: number) => {
    try {
        return (await apiClient.put(`/orders/${id}/validate`, {}, { params: { role: 'ROLE_ADMIN' } })).data
    } catch (error: any) {
        handleApiError(error, 'validateOrder')
    }
}

export const cancelOrder = async (id: number) => {
    try {
        return (await apiClient.put(`/orders/${id}/cancel`)).data
    } catch (error: any) {
        handleApiError(error, 'cancelOrder')
    }
}

export const confirmRefund = async (id: number) => {
    try {
        return (await apiClient.put(`/orders/${id}/refund`, {}, { params: { role: 'ROLE_ADMIN' } })).data
    } catch (error: any) {
        handleApiError(error, 'confirmRefund')
    }
}

export const uploadPaymentProof = async (id: number, file: File) => {
    try {
        const formData = new FormData()
        formData.append('paymentProof', file)
        return (
            await apiClient.post(`/orders/${id}/proof`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
        ).data
    } catch (error: any) {
        handleApiError(error, 'uploadPaymentProof')
    }
}

export const getOrder = async (id: number) => {
    try {
        const response = await apiClient.get(`/orders/${id}`)
        return response.data
    } catch (error: any) {
        console.error('API: Error fetching order:', error.response?.status, error.response?.data)
        handleApiError(error, 'getOrder')
    }
}

export const getUserOrders = async (role?: string) => {
    try {
        return (await apiClient.get('/orders', ...(role ? [{ params: { role } }] : []))).data
    } catch (error: any) {
        handleApiError(error, 'getUserOrders')
    }
}

export const getAllOrders = async () => {
    try {
        return (await apiClient.get('/orders', { params: { role: 'ROLE_ADMIN' } })).data
    } catch (error: any) {
        handleApiError(error, 'getAllOrders')
    }
}

export const getOrderByQrCode = async (qrCodeUuid: string) => {
    try {
        return (await apiClient.get(`/orders/qr/${qrCodeUuid}`, { params: { role: 'ROLE_ADMIN' } })).data
    } catch (error: any) {
        handleApiError(error, 'getOrderByQrCode')
    }
}

export const toggleProductActive = async (id: number, isActive: boolean, lng?: string) => {
    try {
        return (await apiClient.patch(`/products/${id}`, { isActive }, { params: { role: 'ROLE_ADMIN', lng } })).data
    } catch (error: any) {
        handleApiError(error, 'toggleProductActive')
    }
}

export const getShopConfig = async () => {
    try {
        return (await apiClient.get('/orders/config')).data
    } catch (error: any) {
        handleApiError(error, 'getShopConfig')
    }
}

export const createCheckoutIntent = async (
    amount: number,
    itemName: string,
    returnUrl?: string,
    errorUrl?: string,
    backUrl?: string,
) => {
    try {
        const response = await apiClient.post('/orders/checkout-intent', {
            amount,
            itemName,
            returnUrl,
            errorUrl,
            backUrl,
        })

        return response.data
    } catch (error: any) {
        console.error('API CLIENT ERROR:', error)
        console.error('Error response:', error.response?.data)
        handleApiError(error, 'createCheckoutIntent')
    }
}
